// Задания дня (BackEnd/docs/DAILY_PLAN.md + DAILY_INTEGRATION_PLAN.md).
// Состояние раздела приходит ОДНИМ ответом `GET /daily` — фронт ничего не досчитывает.
// Тексты заданий не приходят с сервера (он не знает языка игрока в момент генерации): сервер шлёт `code`,
// экран рисует его через $t('daily.q.' + code) — иначе локализация ломается на генерации набора.
import axios from 'axios';

// Смещение игрока от UTC в минутах: по нему сервер считает локальный день со сбросом в 04:00.
const tzOffset = () => -new Date().getTimezoneOffset();

const state = {
  data: null,
  loading: false,
  error: null,
  errorStatus: 0, // HTTP-код последней неудачи: 0 = сети не было, 401 = сессия не принята сервером
  errorInfo: '',  // техническая расшифровка (код + detail) — показывается под сообщением об ошибке
};

const getters = {
  // Бейдж пункта меню: сколько наград реально можно забрать прямо сейчас (задания + почта).
  dailyBadge: (state) => {
    const d = state.data;
    if (!d) return 0;
    const ready = (d.quests || []).filter((q) => q.ready).length;
    const mail = (d.mail || []).length;
    const choice = d.pending_choice && (d.pending_choice.options || []).length ? 1 : 0;
    return ready + mail + choice;
  },
  dailyPoints: (state) => (state.data ? state.data.points : 0),
  dailyThreshold: (state) => (state.data ? state.data.threshold : 100),
};

const mutations = {
  SET_DAILY: (state, data) => { state.data = data; },
  SET_DAILY_LOADING: (state, v) => { state.loading = v; },
  SET_DAILY_ERROR: (state, e) => {
    state.error = e;
    state.errorStatus = (e && e.response && e.response.status) || 0;
    // Техническая строка для экрана: без неё «сеть недоступна» скрывала настоящую причину
    // (401 от сервера, 500 в обработчике, отказ CORS) и чинить приходилось вслепую.
    const detail = e && e.response && e.response.data && (e.response.data.detail || e.response.data.message);
    state.errorInfo = e ? [state.errorStatus || (e.code || 'network'), detail || e.message].filter(Boolean).join(' · ') : '';
  },
  // Смена игрока на устройстве: без сброса раздел показывал бы прогресс ПРЕЖНЕГО аккаунта
  // (Vuex переживает выход/вход, перезагрузки страницы при этом нет).
  CLEAR_DAILY: (state) => {
    state.data = null;
    state.loading = false;
    state.error = null;
    state.errorStatus = 0;
    state.errorInfo = '';
  },
};

const actions = {
  async fetchDaily({ commit }) {
    commit('SET_DAILY_LOADING', true);
    commit('SET_DAILY_ERROR', null);
    try {
      const r = await axios.get('/daily/', { params: { tz: tzOffset() } });
      commit('SET_DAILY', r.data);
      return r.data;
    } catch (e) {
      commit('SET_DAILY_ERROR', e);
      return null;
    } finally {
      commit('SET_DAILY_LOADING', false);
    }
  },
  // Все мутации возвращают выданное (`granted`) — экран показывает всплывашку и перечитывает состояние.
  async claimQuest({ dispatch }, questId) {
    const r = await axios.post(`/daily/quests/${questId}/claim`);
    await dispatch('fetchDaily');
    return r.data;
  },
  async claimAllQuests({ dispatch }) {
    const r = await axios.post('/daily/quests/claim_all');
    await dispatch('fetchDaily');
    return r.data;
  },
  async rerollQuest({ dispatch }, questId) {
    const r = await axios.post(`/daily/quests/${questId}/reroll`);
    await dispatch('fetchDaily');
    return r.data;
  },
  async claimDailyChest({ dispatch }) {
    const r = await axios.post('/daily/chest');
    await dispatch('fetchDaily');
    return r.data;
  },
  // Награда за закрытую веху месяца: выбор предмета приходит сразу, сундук вехи — в хранилище.
  async claimDailyMonth({ dispatch }) {
    const r = await axios.post('/daily/month');
    await dispatch('fetchDaily');
    return r.data;
  },
  async claimDailyMail({ dispatch }, mailId) {
    const r = await axios.post(`/daily/mail/${mailId}/claim`);
    await dispatch('fetchDaily');
    return r.data;
  },
  async chooseClassItem({ dispatch }, index) {
    const r = await axios.post('/daily/choose', { index });
    await dispatch('fetchDaily');
    return r.data;
  },
};

// Иконки заданий (эмодзи-заглушки — решение оунера: для MVP допустимо). Живут в сторе, потому что
// нужны ДВУМ экранам: разделу `/daily` и окнам итогов партии/поединка в веб-клиенте.
export const QUEST_ICONS = {
  play_match_full: '⚔️', wheel_spin: '🎡', disassemble_2: '🔨', equip_item: '🛡️', heal_unit: '💊',
  swap_positions: '🔀', spectate_match: '👁️', season_join: '🏆', fight_loot_3: '💰', market_list: '🏷️',
  win_match: '👑', fight_streak_3: '🗡️', kill_5: '☠️', both_fractions: '🧿', ability_2: '✨',
  revive_unit: '⚱️', disassemble_5: '⚒️', referral_friend: '🤝', skills_4: '📜', market_sold: '💱',
  king_spell_3: '🔮', win_2_matches: '🏅', fight_streak_6: '🔥', win_flawless: '🛡️', king_before_20: '⏱️',
  equip_full_3: '🎽', season_match: '🏟️', win_vs_stronger: '💪', promote_pawn: '♕',
};

// Снимок прогресса заданий (для дельты «что продвинулось за партию/поединок»).
export const dailySnapshot = (data) => {
  const out = { points: 0, quests: {} };
  if (!data) return out;
  out.points = data.points || 0;
  (data.quests || []).forEach((q) => { out.quests[q.id] = { progress: q.progress || 0, claimed: !!q.claimed }; });
  return out;
};

// Что изменилось между снимком и текущим состоянием: строки для окна итогов + прирост очков.
export const dailyProgressRows = (prev, data, limit = 3) => {
  const rows = [];
  if (!data) return rows;
  (data.quests || []).forEach((q) => {
    const was = (prev && prev.quests && prev.quests[q.id]) || { progress: 0, claimed: false };
    const moved = (q.progress || 0) > was.progress || (!!q.claimed && !was.claimed);
    if (!moved) return;
    rows.push({
      id: q.id,
      code: q.code,
      icon: QUEST_ICONS[q.code] || '📜',
      goal: q.goal,
      progress: q.progress,
      points: q.points,
      done: (q.progress || 0) >= q.goal,
      claimed: !!q.claimed,
      ready: !!q.ready,
    });
  });
  // Сначала завершённые (их награда уже ждёт), затем — самые близкие к цели.
  rows.sort((a, b) => (b.done - a.done) || (b.progress / (b.goal || 1) - a.progress / (a.goal || 1)));
  return rows.slice(0, limit);
};

export default { state, getters, mutations, actions };
